import { Box } from "@mui/material";
import { useTheme, type PaletteMode } from "@mui/material/styles";
import { Chip, Tooltip } from "../ds";
import type { ChipColorVariant } from "../ds";
import {
  statusCategory,
  statusLabel,
  statusShortLabel,
  useWorkflowConfig,
} from "../hooks/useWorkflow";
import type { WorkflowConfig } from "../api/workflow";

export interface CategoryColors {
  bg: string;
  dot: string;
  text: string;
  /**
   * Which of the package's semantic chip tones this triple stands for.
   * `RavenChip` warns in dev when its grey `default` chip carries an icon —
   * and a status chip always carries its dot — so every triple names a tone.
   * It is a marker, not a colour: the `sx` below pins bg/text/dot to the
   * values above.
   */
  tone: ChipColorVariant;
  /** Draw the dot as a ring — see TEAL. */
  ring?: boolean;
}

type Triple = Pick<CategoryColors, "bg" | "dot" | "text">;

/** One status colour, resolved per theme mode by `statusColor`. */
export interface StatusPalette {
  tone: ChipColorVariant;
  ring?: boolean;
  light: Triple;
  dark: Triple;
}

// Map from yaml `canonical.category` to a WCAG-AA-verified palette.
// Keep this small: the category vocabulary is the only thing the frontend
// needs to know about — individual statuses are sourced from the workflow
// config at runtime.
//
// These six palettes are the app’s source of truth for *status* colour —
// the theme models brand and semantic colour, not the per-category
// bg/dot/text sets a chip needs — so they carry raw values and are opted
// out of the `lint:tokens` hex gate line by line. Consumers that need a
// status colour call `statusColor()`; nothing else should restate a value.
//
// Each palette has a light and a dark triple. AA in both modes, pinned by
// StatusChip.test.tsx: text on bg >= 4.5:1, dot on the page and paper
// backgrounds >= 3:1. The dark triples are tinted surfaces under light
// text, lifted for the package's near-black page — the light pastels would
// read as glowing slabs there.
const NEUTRAL: StatusPalette = {
  tone: "info",
  light: { bg: "#F0EFF4", dot: "#5C5F7A", text: "#2C2F47" }, // allow-hex
  dark: { bg: "#262A3A", dot: "#9EA3C4", text: "#DCDEEC" }, // allow-hex
};
// Blue — waiting on a committee (WC / SC review)
const BLUE: StatusPalette = {
  tone: "info",
  light: { bg: "#E4EEF9", dot: "#1565C0", text: "#0D3C73" }, // allow-hex
  dark: { bg: "#12304F", dot: "#5AA3F0", text: "#C4DDF8" }, // allow-hex
};
// Teal — implemented, awaiting custodian closure. Its dot is a ring: teal
// and GREEN (closed) sit side by side in the Status filter and the By-Status
// list and are close in hue, so "not yet closed" is also carried by shape.
const TEAL: StatusPalette = {
  tone: "success",
  ring: true,
  light: { bg: "#E0F2F1", dot: "#00695C", text: "#004D40" }, // allow-hex
  dark: { bg: "#0E3833", dot: "#3CC2AE", text: "#B0EAE0" }, // allow-hex
};
// Red — rejected / deleted
const RED: StatusPalette = {
  tone: "error",
  light: { bg: "#FDECEA", dot: "#C62828", text: "#7F0000" }, // allow-hex
  dark: { bg: "#4A1B1B", dot: "#FF6E6E", text: "#FFCBC7" }, // allow-hex
};
// Green — closed / completed / done
const GREEN: StatusPalette = {
  tone: "success",
  light: { bg: "#E8F5E9", dot: "#2E7D32", text: "#1B5E20" }, // allow-hex
  dark: { bg: "#173D22", dot: "#4CC76A", text: "#BDEBC6" }, // allow-hex
};
// Orange — implementation in progress
const ORANGE: StatusPalette = {
  tone: "warning",
  light: { bg: "#FFF3E0", dot: "#E65100", text: "#BF360C" }, // allow-hex
  dark: { bg: "#4A2A0E", dot: "#F58A3A", text: "#FFD6AE" }, // allow-hex
};

/** Every palette, for the contrast tests. */
export const STATUS_PALETTES = { NEUTRAL, BLUE, TEAL, RED, GREEN, ORANGE };

/** The neutral dot for a mode, so the timeline can recognise it without
 * restating its value. */
export function neutralDot(mode: PaletteMode): string {
  return NEUTRAL[mode].dot;
}

export const CATEGORY_COLORS: Record<string, StatusPalette> = {
  // Generic categories (from workflow config)
  submitted: NEUTRAL,
  draft: NEUTRAL,
  pending: NEUTRAL,
  reported: NEUTRAL,
  pending_approval: BLUE,
  in_review: BLUE,
  in_progress: ORANGE,
  approved: TEAL,
  rejected: RED,
  closed: GREEN,
  completed: GREEN,
  done: GREEN,
  deleted: RED,

  // ESS status names, matched before the category so each stage gets its
  // own colour even where the yaml category is broad (accepted_by_wc/sc are
  // both `in_progress`). One colour per `all_suggestions` facet group —
  // the Status filter paints a group with its states' shared dot, so a
  // group's states must never disagree:
  //   Submitted · Custodian   submitted                        NEUTRAL
  //   Under WC / SC Review   accepted_by_hr, accepted_by_wc   BLUE
  //   With HOF/HOD · Impl.    accepted_by_sc                   ORANGE
  //   Implemented · Awaiting  implemented, under_verification  TEAL
  //   Closed · Implemented    implementation_closed            GREEN
  //   Rejected                rejected_by_hr/wc/sc             RED
  // accepted_by_sc was TEAL ("approved") and implemented ORANGE, which put
  // the in-progress colour on a finished stage and split the Awaiting-closure
  // group across two colours; orange now marks the one stage where work is
  // actually under way.
  accepted_by_hr: BLUE,
  accepted_by_wc: BLUE,
  accepted_by_sc: ORANGE,
  rejected_by_hr: RED,
  rejected_by_wc: RED,
  rejected_by_sc: RED,
  implementation_closed: GREEN,
  implemented: TEAL,
  under_verification: TEAL,
};

/**
 * Resolve the color triple for a status in a theme mode —
 * workflow-config-driven. Helper for surfaces that render their own
 * chips/dots (filter dropdown, By-Status breakdown bars); pass
 * `theme.palette.mode`.
 */
export function statusColor(
  config: WorkflowConfig | undefined,
  status: string,
  mode: PaletteMode,
): CategoryColors {
  const cat = statusCategory(config, status);
  const palette = CATEGORY_COLORS[status] ?? CATEGORY_COLORS[cat] ?? NEUTRAL;
  return { ...palette[mode], tone: palette.tone, ring: palette.ring };
}

const visuallyHidden = {
  position: "absolute",
  // Strings, not numbers: sx reads 0 < n <= 1 as a percentage, so `width: 1`
  // became 100% and widened the page by the chip's full label.
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
  p: 0,
  m: "-1px",
} as const;

interface Props {
  status: string;
  /**
   * Render the state's `short_label` from the workflow yaml instead of
   * the full canonical label. Falls back to the full label when the
   * yaml has no short_label for the state.
   */
  short?: boolean;
  /** 20px tall at 11.5px type, for dense lists such as the rail timeline. */
  dense?: boolean;
}

export function StatusDot({ color, ring }: { color: string; ring?: boolean }) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: 8,
        height: 8,
        boxSizing: "border-box",
        borderRadius: "50%",
        ...(ring
          ? { border: `2px solid ${color}` }
          : { backgroundColor: color }),
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

export default function StatusChip({ status, short, dense = false }: Props) {
  const { data: config } = useWorkflowConfig();
  const theme = useTheme();
  const colors = statusColor(config, status, theme.palette.mode);
  const fullLabel = statusLabel(config, status) || status;
  const label = short ? statusShortLabel(config, status) || fullLabel : fullLabel;

  // The full label is always in the accessible name and in a tooltip: the
  // short label hides it outright, and a full label can still ellipsize in
  // a narrow column. Only a short chip takes a tab stop — it is the one
  // whose tooltip carries text the eye cannot otherwise get.
  const hidesText = label !== fullLabel;
  const chip = (
    <Chip
      label={
        hidesText ? (
          <>
            <span aria-hidden="true">{label}</span>
            <Box component="span" sx={visuallyHidden}>
              {fullLabel}
            </Box>
          </>
        ) : (
          label
        )
      }
      colorVariant={colors.tone}
      icon={<StatusDot color={colors.dot} ring={colors.ring} />}
      sx={{
        pl: dense ? "6px" : "8px",
        backgroundColor: colors.bg,
        color: colors.text,
        "& .MuiChip-label": { color: colors.text },
        "& .MuiChip-icon": { ml: 0, mr: 0 },
        ...(dense && {
          height: 20,
          fontSize: 11.5,
          "& .MuiChip-label": {
            color: colors.text,
            px: "5px",
            lineHeight: "20px",
          },
        }),
      }}
    />
  );

  return (
    <Tooltip title={fullLabel} placement="top" describeChild>
      <Box
        component="span"
        tabIndex={hidesText ? 0 : undefined}
        sx={{
          display: "inline-flex",
          maxWidth: "100%",
          borderRadius: 999,
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        {chip}
      </Box>
    </Tooltip>
  );
}
